import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Space, Alert, Statistic, Row, Col, Table, Tag } from 'antd';
import { PlayCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { validatePhenoAgeImplementation, calculateLevinePhenoAge } from '../utils/riskCalculators';

const { Title, Text, Paragraph } = Typography;

const PhenoAgeValidation = () => {
  const [validationResults, setValidationResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState('');

  // Capture console output
  useEffect(() => {
    const originalLog = console.log;
    const logs = [];
    
    console.log = (...args) => {
      logs.push(args.join(' '));
      originalLog(...args);
    };

    return () => {
      console.log = originalLog;
      if (logs.length > 0) {
        setConsoleOutput(logs.join('\n'));
      }
    };
  }, []);

  const runValidation = async () => {
    setIsRunning(true);
    setConsoleOutput('');
    
    try {
      // Clear previous console output
      const logs = [];
      const originalLog = console.log;
      
      console.log = (...args) => {
        logs.push(args.join(' '));
        originalLog(...args);
      };
      
      const results = validatePhenoAgeImplementation();
      
      console.log = originalLog;
      setConsoleOutput(logs.join('\n'));
      setValidationResults(results);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const testExcelReference = () => {
    const testCase = {
      albumin: 4.6,
      creatinine: 0.77,
      glucose: 106,
      cReactiveProtein: 0.81,
      lymphocytePercent: 33,
      meanCellVolume: 94,
      redCellDistributionWidth: 12.3,
      alkalinePhosphatase: 66,
      whiteBloodCellCount: 4.4,
      chronologicalAge: 72.18
    };

    const result = calculateLevinePhenoAge(testCase);
    console.log('🧬 Excel Reference Test Results:');
    console.log(`  PhenoAge: ${result.phenoAge} years (Expected: 60.24)`);
    console.log(`  Age Difference: ${result.ageDifference} years (Expected: -11.94)`);
    console.log(`  Error: ${Math.abs(result.phenoAge - 60.24).toFixed(3)} years`);
    console.log(`  Status: ${Math.abs(result.phenoAge - 60.24) < 0.5 ? '✅ PASS' : '❌ FAIL'}`);
    console.log('Full result object:', result);
    return result;
  };

  const runQuickValidation = () => {
    console.log('🚀 Running Quick PhenoAge Validation...');

    // Test Excel reference
    const excelTest = testExcelReference();

    // Test sensitivity for key parameters
    const testCase = {
      albumin: 4.6, creatinine: 0.77, glucose: 106, cReactiveProtein: 0.81,
      lymphocytePercent: 33, meanCellVolume: 94, redCellDistributionWidth: 12.3,
      alkalinePhosphatase: 66, whiteBloodCellCount: 4.4, chronologicalAge: 72.18
    };

    console.log('\n🔍 Quick Sensitivity Analysis (5% perturbations):');
    ['glucose', 'redCellDistributionWidth', 'whiteBloodCellCount'].forEach(param => {
      const testUp = { ...testCase, [param]: testCase[param] * 1.05 };
      const testDown = { ...testCase, [param]: testCase[param] * 0.95 };

      const resultUp = calculateLevinePhenoAge(testUp);
      const resultDown = calculateLevinePhenoAge(testDown);

      const avgImpact = (Math.abs(resultUp.phenoAge - excelTest.phenoAge) +
                        Math.abs(resultDown.phenoAge - excelTest.phenoAge)) / 2;

      console.log(`  ${param}: ±${avgImpact.toFixed(2)} years impact`);
    });

    console.log('\n✅ Quick validation complete! Check console for details.');
  };

  const renderValidationStatus = (status, label) => {
    const icon = status ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ExclamationCircleOutlined style={{ color: '#f5222d' }} />;
    const color = status ? '#52c41a' : '#f5222d';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon}
        <Text style={{ color }}>{label}: {status ? 'PASS' : 'FAIL'}</Text>
      </div>
    );
  };

  const sensitivityColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 60,
    },
    {
      title: 'Parameter',
      dataIndex: 'param',
      key: 'param',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Impact (±5%)',
      dataIndex: 'avgImpact',
      key: 'avgImpact',
      render: (value) => (
        <Tag color={value > 2 ? 'red' : value > 1 ? 'orange' : 'green'}>
          ±{value.toFixed(2)} years
        </Tag>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Card>
        <Title level={2}>
          🧬 PhenoAge Implementation Validation
        </Title>
        <Paragraph>
          Comprehensive mathematical validation and sensitivity analysis of the Care360 PhenoAge biological age calculation.
          This analysis verifies accuracy against the Excel reference and tests clinical robustness.
        </Paragraph>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Button 
              type="primary" 
              icon={<PlayCircleOutlined />}
              onClick={runValidation}
              loading={isRunning}
              size="large"
            >
              Run Comprehensive Validation
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={testExcelReference}
            >
              Test Excel Reference
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={runQuickValidation}
              type="default"
            >
              Quick Validation
            </Button>
          </div>

          {validationResults && (
            <>
              <Alert
                message="Validation Complete"
                description={`Analysis completed with ${validationResults.recommendations.length} recommendations`}
                type="success"
                showIcon
              />

              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Mathematical Accuracy"
                      value={validationResults.mathematicalVerification.baselineTest.error.toFixed(3)}
                      suffix="years error"
                      valueStyle={{ 
                        color: validationResults.mathematicalVerification.baselineTest.withinTolerance ? '#3f8600' : '#cf1322' 
                      }}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Numerical Precision"
                      value={validationResults.numericalPrecision.standardDeviation.toFixed(6)}
                      suffix="std dev"
                      valueStyle={{ 
                        color: validationResults.numericalPrecision.isStable ? '#3f8600' : '#cf1322' 
                      }}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Sensitivity Parameters"
                      value={validationResults.sensitivityAnalysis.rankings.length}
                      suffix="tested"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
              </Row>

              <Card title="Validation Summary" size="small">
                <Space direction="vertical">
                  {renderValidationStatus(
                    validationResults.mathematicalVerification.baselineTest.withinTolerance,
                    'Mathematical Accuracy'
                  )}
                  {renderValidationStatus(
                    validationResults.clinicalValidation.clinicallyStable,
                    'Clinical Stability'
                  )}
                  {renderValidationStatus(
                    validationResults.numericalPrecision.isStable,
                    'Numerical Precision'
                  )}
                </Space>
              </Card>

              {validationResults.sensitivityAnalysis.rankings.length > 0 && (
                <Card title="Parameter Sensitivity Rankings" size="small">
                  <Table
                    dataSource={validationResults.sensitivityAnalysis.rankings.map((item, index) => ({
                      key: index,
                      rank: index + 1,
                      param: item.param,
                      avgImpact: item.avgImpact
                    }))}
                    columns={sensitivityColumns}
                    pagination={false}
                    size="small"
                  />
                </Card>
              )}

              <Card title="Recommendations" size="small">
                <Space direction="vertical">
                  {validationResults.recommendations.map((rec, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      {rec.includes('✓') ? (
                        <CheckCircleOutlined style={{ color: '#52c41a', marginTop: 4 }} />
                      ) : rec.includes('❌') ? (
                        <ExclamationCircleOutlined style={{ color: '#f5222d', marginTop: 4 }} />
                      ) : (
                        <WarningOutlined style={{ color: '#faad14', marginTop: 4 }} />
                      )}
                      <Text>{rec.replace(/^[✓❌⚠️]\s*/, '')}</Text>
                    </div>
                  ))}
                </Space>
              </Card>
            </>
          )}

          {consoleOutput && (
            <Card title="Detailed Analysis Log" size="small">
              <pre style={{ 
                background: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '4px',
                fontSize: '12px',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {consoleOutput}
              </pre>
            </Card>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default PhenoAgeValidation;
